import React, { memo, useMemo, useState, useContext, useEffect } from 'react';
import { isEqual } from 'lodash-es';
import { useRequest } from 'ahooks';
import { H3 } from 'tea-component';
import { context as Context } from '@gp/components/Context';
import TriggerViewCard from '@gp/components/TriggerViewCard';
// import SvgIcon from '@gp/components/Iconfont';
import MergeableTable from './components/MergeableTable';
import TableForPDFDownload from './components/MergeableTable/TableForPDFDownload';
import LineEchartCon from './components/LineEchartCon';
import BarEchartCon from './components/BarEchartCon';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import useFinancialsList from '../../hooks/useFinancialsList';
import {
  queryMiddleLongTimeFinancialsDataOfFinance,
  downloadMiddleLongTimeFinancialsDataOfFinance,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import { getStudioModuleName } from '../../../../../utils';
import { FILE_TYPES1 } from '@gp/constants/const';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import CardBtn from '@/pages/globalpedia/components/CardBtn';
import DataTime from '@/pages/globalpedia/components/DataTime';
import { formatDateStr } from '@gp/utils/common';

interface FinancialsPropsType extends IBaseProps { }

// Long Term Financials模块名称
const MODULE_NAME = 'Long Term Financials';

// Long Term Financials模块 PDF下载 需要用到的className
const PDF_DOWNLOAD_NODE_CLASSNAME =
  'dbg-studio-overall-details-finance-financials-pdf-card';

const DEFAULT_PROPS: FinancialsPropsType = {};

function Financials(props: FinancialsPropsType) {
  const { } = props;

  const { studioId, studioName }: StudioOverallDetailContextTypes =
    useContext(Context) || {};

  const [dataList, setDataList] = useState<orgHeadcountsDataTypes>(null);

  const {
    loading: middleLongTimeFinancialsLoading,
    data: middleLongTimeFinancialsData,
    run,
  } = useRequest(queryMiddleLongTimeFinancialsDataOfFinance, { manual: true });

  const middleLongTimeFinancialsDataUpdateTIme = formatDateStr(middleLongTimeFinancialsData?.update_time);

  // 处理接口数据
  const { orgData } = useFinancialsList({
    loading: middleLongTimeFinancialsLoading,
    data: middleLongTimeFinancialsData?.trend,
  });

  const { isDownloading, setIsDownloading, handleDownload } = useDownload({
    fileName: getStudioModuleName(MODULE_NAME, studioName),
    csvDownloadLogKey:
      'STUDIO_OVERALL_DETAIL_FINANCE_MIDDLE_LONG_TIME_FINANCIALS_CSV_SUBMIT',
    pdfDownloadLogKey:
      'STUDIO_OVERALL_DETAIL_FINANCE_MIDDLE_LONG_TIME_FINANCIALS_PDF_SUBMIT',
    node: document.querySelector(`.${PDF_DOWNLOAD_NODE_CLASSNAME}`),
    downloadService: createDownloadService(
      downloadMiddleLongTimeFinancialsDataOfFinance,
    ),
  });

  // 月份筛选器发生变化
  useEffect(() => {
    // 请求接口
    run({
      studio_id: studioId,
    });
  }, []);

  // 接口返回数据
  useEffect(() => {
    if (middleLongTimeFinancialsLoading) return;
    setDataList(orgData);
  }, [orgData]);

  const tabs = useMemo(() => {
    return [
      {
        id: 'chart-bar',
        label: <CardBtn onClick={() => { }} type="column-chart" />,
        component: <BarEchartCon />,
      },
      {
        id: 'chart-line',
        label: <CardBtn onClick={() => { }} type="discount-chart" />,
        component: <LineEchartCon />,
      },
      {
        id: 'table',
        label: <CardBtn onClick={() => { }} type="sheet" />,
        component: <MergeableTable />,
      },
      {
        id: 'download',
        label: (
          <GeneralDownloadBtn
            uiType="2"
            fileTypes={FILE_TYPES1}
            isLoading={isDownloading}
            onConfirm={async (payload) => {
              if (isDownloading) return;
              const params = {
                ...payload,
                file_type: Number.parseInt(payload?.fileType, 10),
                studio_id: studioId,
              };
              handleDownload?.(params);
            }}
          />
        ),
        component: null,
      },
    ];
  }, [studioId, isDownloading, setIsDownloading, handleDownload]);

  const pdfTabs = useMemo(() => {
    return [
      {
        id: 'table',
        label: null,
        component: <TableForPDFDownload />,
      },
    ];
  }, []);

  return (
    <Context.Provider
      value={{
        loading: middleLongTimeFinancialsLoading,
        dataList,
        isAllDatasResLoading: middleLongTimeFinancialsLoading,
        allDatas: dataList,
        updateTime: middleLongTimeFinancialsDataUpdateTIme,
      }}
    >
      <div className="tea-card dbg-studio-overall-details-finance-financials-card-container">
        <TriggerViewCard
          className="dbg-studio-overall-details-finance-financials-content"
          title={(
            <H3 className='dbg-financials-content-title'>
              {MODULE_NAME}
              {!!middleLongTimeFinancialsDataUpdateTIme && (
                <DataTime
                  className="dbg-financials-content-time"
                  dateType='Month'
                  date={middleLongTimeFinancialsDataUpdateTIme}
                />
              )}
            </H3>
          )}
          loading={middleLongTimeFinancialsLoading}
          isNoData={false}
          tabs={tabs}
          defaultActiveId="chart-bar"
        />
        <TriggerViewCard
          className="dbg-studio-overall-details-finance-financials-content"
          // 这个rootNodeClassName 是为了 下载PDF用的 不要删除
          rootNodeClassName={PDF_DOWNLOAD_NODE_CLASSNAME}
          rootNodeStyle={{ marginTop: 0 }}
          title={<H3 className='dbg-financials-content-title'>{MODULE_NAME}<DataTime className="dbg-financials-content-time" dateType='Month' date={'2024-02'} /></H3>}
          loading={middleLongTimeFinancialsLoading}
          isNoData={false}
          tabs={pdfTabs}
          defaultActiveId="table"
        />
      </div>
    </Context.Provider>
  );
}

Financials.defaultProps = DEFAULT_PROPS;

export default memo(Financials, (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
});
